import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
    getProjectReport,
    getProjectReportHtml,
    ProjectResponseReport,
    ReportGroup,
    ReportContent
} from '../../Services/ProjectReportService';
import { getProjectPersonalityTraitsChallenges, PersonalityTraitsChallenge } from '../../Services/PersonalityTraitsChallengeService';
import { getProjectVirtualSpacesChallenges, VirtualSpacesChallenge } from '../../Services/VirtualSpacesChallengeService';
import { getProjectLandscapeAndTexturesChallenges, LandscapeAndTexturesChallenge } from '../../Services/LandscapeAndTexturesChallengeService';
import { getProjectHandDoesntLieChallenges, HandDoesntLieChallenge } from '../../Services/HandDoesntLieChallengeService';
import { getProjectSpatialExperienceChallenges, SpatialExperienceChallenge } from '../../Services/SpatialExperienceChallengeService';
import { getProjectSpatialExperienceRessoConfigChallenges, SpatialExperienceRessoConfigChallenge } from '../../Services/SpatialExperienceRessoConfigChallengeService';
import { getProjectSpatialExperienceRessoChallenges, SpatialExperienceRessoChallenge } from '../../Services/SpatialExperienceRessoChallengeService';
import { getAllReports } from '../../Services/EditableReportService';
import { buildGroupPdfMap } from '../../utils/reportPdfMap';

const ProjectReport: React.FC = () => {
    const { projectId } = useParams<{ projectId: string }>();
    const [report, setReport] = useState<ProjectResponseReport | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [expandedGroups, setExpandedGroups] = useState<string[]>([]);
    const [expandedSections, setExpandedSections] = useState<string[]>([]);
    const [isDownloading, setIsDownloading] = useState(false);
    const [error, setError] = useState<string>('');
    const [groupPdfUrls, setGroupPdfUrls] = useState<Record<string, string>>({});

    // Challenge data loaded from dedicated services
    const [challengeData, setChallengeData] = useState<{
        personalityTraits: PersonalityTraitsChallenge[];
        virtualSpaces: VirtualSpacesChallenge[];
        landscapeAndTextures: LandscapeAndTexturesChallenge[];
        handDoesntLie: HandDoesntLieChallenge[];
        spatialExperience: SpatialExperienceChallenge[];
        spatialExperienceRessoConfig: SpatialExperienceRessoConfigChallenge[];
        spatialExperienceResso: SpatialExperienceRessoChallenge[];
    }>({
        personalityTraits: [],
        virtualSpaces: [],
        landscapeAndTextures: [],
        handDoesntLie: [],
        spatialExperience: [],
        spatialExperienceRessoConfig: [],
        spatialExperienceResso: []
    });

    const fetchReport = useCallback(async () => {
        if (!projectId) return;

        setIsLoading(true);
        setError('');
        try {
            // Fetch report structure and all challenge data in parallel
            const [
                reportData,
                personalityTraits,
                virtualSpaces,
                landscapeAndTextures,
                handDoesntLie,
                spatialExperience,
                spatialExperienceRessoConfig,
                spatialExperienceResso
            ] = await Promise.all([
                getProjectReport(projectId),
                getProjectPersonalityTraitsChallenges(projectId).catch(() => []),
                getProjectVirtualSpacesChallenges(projectId).catch(() => []),
                getProjectLandscapeAndTexturesChallenges(projectId).catch(() => []),
                getProjectHandDoesntLieChallenges(projectId).catch(() => []),
                getProjectSpatialExperienceChallenges(projectId).catch(() => []),
                getProjectSpatialExperienceRessoConfigChallenges(projectId).catch(() => []),
                getProjectSpatialExperienceRessoChallenges(projectId).catch(() => [])
            ]);

            console.log('📊 Challenge data loaded:', {
                personalityTraits: personalityTraits.length,
                virtualSpaces: virtualSpaces.length,
                landscapeAndTextures: landscapeAndTextures.length,
                handDoesntLie: handDoesntLie.length,
                spatialExperience: spatialExperience.length,
                spatialExperienceRessoConfig: spatialExperienceRessoConfig.length,
                spatialExperienceResso: spatialExperienceResso.length
            });

            setChallengeData({
                personalityTraits,
                virtualSpaces,
                landscapeAndTextures,
                handDoesntLie,
                spatialExperience,
                spatialExperienceRessoConfig,
                spatialExperienceResso
            });

            setReport(reportData);
            // Expand first group by default
            if (reportData.groups.length > 0) {
                setExpandedGroups([reportData.groups[0].group_id]);
            }

            // Load PDF URLs from editable reports
            try {
                const editableReports = await getAllReports();
                // Cada EditableReport está asociado a UN grupo del proyecto, así que
                // hay varios reports por proyecto (uno por envío). Antes se usaba
                // .find (solo el primero) y el botón de descarga aparecía en un único
                // grupo; buildGroupPdfMap recoge TODOS los reports del proyecto.
                setGroupPdfUrls(buildGroupPdfMap(editableReports, projectId ?? ''));
            } catch (err) {
                console.error('Error loading PDF URLs:', err);
            }
        } catch (err: unknown) {
            const errorMessage = err instanceof Error ? err.message : 'Error al cargar el reporte';
            setError(errorMessage);
        } finally {
            setIsLoading(false);
        }
    }, [projectId]);

    useEffect(() => {
        fetchReport();
    }, [fetchReport]);

    const toggleGroup = (groupId: string) => {
        setExpandedGroups(prev =>
            prev.includes(groupId)
                ? prev.filter(id => id !== groupId)
                : [...prev, groupId]
        );
    };

    const toggleSection = (sectionId: string) => {
        setExpandedSections(prev =>
            prev.includes(sectionId)
                ? prev.filter(id => id !== sectionId)
                : [...prev, sectionId]
        );
    };

    const handleDownloadPdf = async () => {
        if (!projectId) return;

        setIsDownloading(true);
        try {
            const html = await getProjectReportHtml(projectId);
            const blob = new Blob([html], { type: 'text/html; charset=utf-8' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            const filename = report?.project_name
                ? `reporte-${report.project_name.toLowerCase().replace(/\s+/g, '-')}.html`
                : `reporte-proyecto-${projectId}.html`;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (err: unknown) {
            console.error('Error downloading report:', err);
            setError('Error al descargar el informe');
        } finally {
            setIsDownloading(false);
        }
    };

    const getContentTypeLabel = (contentType: string) => {
        const labels: Record<string, string> = {
            'TEXT_SIMPLE': 'Texto',
            'TEXT_SELECTABLE': 'Seleccionable',
            'QUESTION_SIMPLE': 'Pregunta Simple',
            'QUESTION_MULTIPLE': 'Opcion Multiple',
            'QUESTION_IMAGE': 'Imagen',
            'CHALLENGE': 'Challenge',
            'IMAGE_COMMENT': 'Imagen y Comentario'
        };
        const label = labels[contentType] || contentType;
        return <span className="text-muted fst-italic ms-2">({label})</span>;
    };

    const getQuestionText = (question: Record<string, unknown>): string => {
        if (!question) return '';
        // QUESTION_SIMPLE / IMAGE_COMMENT: { singular: "...", original: "..." }
        if (typeof question.singular === 'string') return question.singular;
        if (typeof question.original === 'string') return question.original;
        // QUESTION_MULTIPLE: { questionText: { singular: "...", original: "..." } }
        if (question.questionText && typeof question.questionText === 'object') {
            const qt = question.questionText as Record<string, unknown>;
            if (typeof qt.singular === 'string') return qt.singular;
            if (typeof qt.original === 'string') return qt.original;
        }
        return '';
    };

    const stripHtml = (html: string): string => {
        return html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    };

    const getProjectTypeText = (type: string): string => {
        switch (type) {
            case 'NEW_CONSTRUCTION': return 'Obra Nueva';
            case 'RENOVATION': return 'Reforma';
            default: return type || 'Desconocido';
        }
    };

    const getSurfaceText = (surface: string): string => {
        switch (surface) {
            case 'AREA_LOWER_THAN_50': return '< 50m²';
            case 'AREA_BETWEEN_51_AND_100': return '51 - 100m²';
            case 'AREA_BETWEEN_101_AND_200': return '101 - 200m²';
            case 'AREA_BETWEEN_201_AND_500': return '201 - 500m²';
            case 'AREA_GREATER_THAN_500': return '> 500m²';
            default: return surface || 'Desconocido';
        }
    };

    // Helper to get challenge data by type from loaded state
    const getChallengeDataByType = (challengeType: string) => {
        switch (challengeType) {
            case 'PERSONALITY_TRAITS':
                return challengeData.personalityTraits;
            case 'VIRTUAL_SPACES':
            case 'VIRTUAL_SPACES_SCORING':
                return challengeData.virtualSpaces;
            case 'LANDSCAPE_AND_TEXTURES':
                return challengeData.landscapeAndTextures;
            case 'HAND_DOESNT_LIE':
                return challengeData.handDoesntLie;
            case 'SPATIAL_EXPERIENCE':
                return challengeData.spatialExperience;
            case 'SPATIAL_EXPERIENCE_RESSO_CONFIG':
                return challengeData.spatialExperienceRessoConfig;
            case 'SPATIAL_EXPERIENCE_RESSO':
                return challengeData.spatialExperienceResso;
            default:
                return [];
        }
    };

    // Render challenge data inline
    const renderChallengeContent = (content: ReportContent) => {
        const challengeType = content.question?.challengeType as string;
        const data = getChallengeDataByType(challengeType);

        if (!data || data.length === 0) {
            return <span className="text-muted fst-italic">Sin datos de challenge</span>;
        }

        switch (challengeType) {
            case 'PERSONALITY_TRAITS':
                return (
                    <div className="row">
                        {(data as PersonalityTraitsChallenge[]).map((challenge) => {
                            let traitsDisplay = challenge.traits;
                            try {
                                const parsed = JSON.parse(challenge.traits);
                                if (parsed.adjectives) {
                                    traitsDisplay = parsed.adjectives.join(', ');
                                }
                            } catch { /* keep original */ }
                            return (
                                <div className="col-md-6 mb-2" key={challenge.personality_traits_challenge_id}>
                                    <div className="card h-100 border-primary">
                                        <div className="card-body py-2">
                                            {challenge.member_name && (
                                                <p className="mb-1"><strong>{challenge.member_name}</strong></p>
                                            )}
                                            <p className="mb-1"><strong>Rasgos:</strong> {traitsDisplay}</p>
                                            <p className="mb-0"><strong>Completado juntos:</strong> {challenge.together ? 'Si' : 'No'}</p>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                );

            case 'VIRTUAL_SPACES':
                return (
                    <div>
                        {(data as VirtualSpacesChallenge[]).map((challenge) => {
                            let points: Array<{x: number, y: number, id: string, label: string}> = [];
                            try {
                                if (challenge.points && challenge.points !== '[]') {
                                    points = JSON.parse(challenge.points);
                                }
                            } catch { /* ignore parse errors */ }

                            return (
                                <div className="card mb-3 border-info" key={challenge.virtual_spaces_challenge_id}>
                                    <div className="card-body py-2">
                                        <div className="row">
                                            {challenge.image_url && (
                                                <div className="col-md-6 mb-2 mb-md-0">
                                                    <div style={{ position: 'relative', display: 'inline-block' }}>
                                                        <img
                                                            src={challenge.image_url}
                                                            alt="Virtual Space"
                                                            className="img-fluid rounded"
                                                        />
                                                        {points.map((point) => (
                                                            <div
                                                                key={point.id}
                                                                style={{
                                                                    position: 'absolute',
                                                                    left: `${point.x}%`,
                                                                    top: `${point.y}%`,
                                                                    transform: 'translate(-50%, -100%)',
                                                                    backgroundColor: 'rgba(220, 53, 69, 0.9)',
                                                                    borderRadius: '4px',
                                                                    padding: '2px 6px',
                                                                    color: 'white',
                                                                    fontSize: '10px',
                                                                    fontWeight: 'bold',
                                                                    whiteSpace: 'nowrap'
                                                                }}
                                                            >
                                                                {point.label}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                            <div className={challenge.image_url ? 'col-md-6' : 'col-12'}>
                                                {challenge.comment && <p className="mb-2 small">{challenge.comment}</p>}
                                                <p className="mb-0 small text-muted">
                                                    Acogedor: {challenge.cozy} | Espacioso: {challenge.spacious} | Luminoso: {challenge.bright}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                );

            case 'VIRTUAL_SPACES_SCORING':
                return (
                    <div>
                        {(data as VirtualSpacesChallenge[]).map((challenge) => (
                            <div className="card mb-2 border-info" key={challenge.virtual_spaces_challenge_id}>
                                <div className="card-body py-2">
                                    <div className="row align-items-center">
                                        {challenge.image_url && (
                                            <div className="col-auto">
                                                <img
                                                    src={challenge.image_url}
                                                    alt="Virtual Space"
                                                    className="rounded"
                                                    style={{ maxHeight: '60px' }}
                                                />
                                            </div>
                                        )}
                                        <div className="col">
                                            <p className="mb-0 small">
                                                <strong>Acogedor:</strong> {challenge.cozy} | <strong>Espacioso:</strong> {challenge.spacious} | <strong>Luminoso:</strong> {challenge.bright}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                );

            case 'LANDSCAPE_AND_TEXTURES':
                return (
                    <div>
                        {(data as LandscapeAndTexturesChallenge[]).map((challenge) => {
                            const activities = [1, 2, 3].map(n => ({
                                num: n,
                                activity: challenge[`activity${n}` as keyof typeof challenge] as string,
                                image: challenge[`image_activity${n}` as keyof typeof challenge] as string,
                                texture: challenge[`texture_activity${n}` as keyof typeof challenge] as string,
                                coords: challenge[`coords_activity${n}` as keyof typeof challenge] as string,
                                answer: challenge[`answer_activity${n}` as keyof typeof challenge] as string,
                                textureAnswer: challenge[`texture_answer_activity${n}` as keyof typeof challenge] as string,
                            })).filter(a => a.activity);

                            return (
                                <div key={challenge.landscape_and_textures_challenge_id} className="mb-3">
                                    <p className="mb-2"><strong>Habitacion:</strong> {challenge.room}</p>
                                    {activities.map(act => {
                                        const [x, y] = act.coords ? act.coords.split(',').map(Number) : [0, 0];
                                        return (
                                            <div key={act.num} className="card mb-2 border-success">
                                                <div className="card-body py-2">
                                                    <p className="mb-2"><strong>Actividad {act.num}:</strong> {act.activity}</p>
                                                    <div className="row">
                                                        {act.image && (
                                                            <div className="col-md-6 mb-2">
                                                                <p className="small text-muted mb-1">Paisaje:</p>
                                                                <div style={{ position: 'relative', display: 'inline-block' }}>
                                                                    <img
                                                                        src={`/landscapes/${act.image}`}
                                                                        alt={`Paisaje ${act.num}`}
                                                                        className="img-fluid rounded"
                                                                        style={{ maxHeight: '150px' }}
                                                                    />
                                                                    {act.coords && (
                                                                        <div
                                                                            style={{
                                                                                position: 'absolute',
                                                                                left: `${x}%`,
                                                                                top: `${y}%`,
                                                                                transform: 'translate(-50%, -100%)',
                                                                                backgroundColor: 'rgba(220, 53, 69, 0.9)',
                                                                                borderRadius: '4px',
                                                                                padding: '2px 6px',
                                                                                color: 'white',
                                                                                fontSize: '10px',
                                                                                fontWeight: 'bold',
                                                                                whiteSpace: 'nowrap',
                                                                                maxWidth: '120px',
                                                                                overflow: 'hidden',
                                                                                textOverflow: 'ellipsis'
                                                                            }}
                                                                            title={act.activity}
                                                                        >
                                                                            {act.activity}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                                {act.answer && <p className="small mt-1 mb-0">{act.answer}</p>}
                                                            </div>
                                                        )}
                                                        {act.texture && (
                                                            <div className="col-md-6 mb-2">
                                                                <p className="small text-muted mb-1">Textura:</p>
                                                                <img
                                                                    src={`/textures/${act.texture}`}
                                                                    alt={`Textura ${act.num}`}
                                                                    className="img-fluid rounded"
                                                                    style={{ maxHeight: '150px' }}
                                                                />
                                                                {act.textureAnswer && <p className="small mt-1 mb-0">{act.textureAnswer}</p>}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            );
                        })}
                    </div>
                );

            case 'HAND_DOESNT_LIE':
                return (
                    <div>
                        {(data as HandDoesntLieChallenge[]).map((challenge) => {
                            const images = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]
                                .map(i => challenge[`image_url_${i}` as keyof typeof challenge] as string)
                                .filter(Boolean);
                            return (
                                <div className="card mb-3 border-warning" key={challenge.hand_doesnt_lie_challenge_id}>
                                    <div className="card-body py-3">
                                        {challenge.member_name && (
                                            <p className="mb-2"><strong>{challenge.member_name}</strong></p>
                                        )}
                                        <p className="mb-2 text-muted small">Imágenes subidas: {images.length}/10</p>
                                        <div className="row g-3">
                                            {images.map((url, idx) => (
                                                <div key={idx} className="col-6 col-md-4 col-lg-3">
                                                    <div className="position-relative">
                                                        <img
                                                            src={url}
                                                            alt={`Dibujo ${idx + 1}`}
                                                            className="img-fluid rounded shadow-sm"
                                                            style={{ width: '100%', aspectRatio: '1', objectFit: 'cover' }}
                                                        />
                                                        <a
                                                            href={url}
                                                            download={`dibujo-${idx + 1}.jpg`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="btn btn-sm btn-dark position-absolute"
                                                            style={{ bottom: '5px', right: '5px', opacity: 0.8 }}
                                                            title="Descargar imagen"
                                                        >
                                                            <i className="bi bi-download"></i>
                                                        </a>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                );

            case 'SPATIAL_EXPERIENCE':
                // Sort: WORKING first, then by scene_number
                const sortedSpatialExperience = [...(data as SpatialExperienceChallenge[])].sort((a, b) => {
                    // WORKING (laborable) first, then HOLIDAY (festivo)
                    if (a.day_type !== b.day_type) {
                        return a.day_type === 'WORKING' ? -1 : 1;
                    }
                    // Then by scene number
                    return a.scene_number - b.scene_number;
                });
                return (
                    <div>
                        {sortedSpatialExperience.map((challenge) => {
                            let positions: Array<{memberId: string, memberName: string, x: number, y: number, color: string}> = [];
                            try {
                                if (challenge.positions && challenge.positions !== '[]') {
                                    positions = JSON.parse(challenge.positions);
                                }
                            } catch { /* ignore parse errors */ }

                            let comments: Array<{memberName: string, zone: string, answer: string}> = [];
                            try {
                                if (challenge.scene_comments && challenge.scene_comments !== '[]') {
                                    comments = JSON.parse(challenge.scene_comments);
                                }
                            } catch { /* ignore parse errors */ }

                            return (
                                <div className="card mb-3 border-primary" key={challenge.spatial_experience_challenge_id}>
                                    <div className="card-body py-2">
                                        <div className="d-flex justify-content-between align-items-start mb-2">
                                            <div>
                                                <strong>Escena {challenge.scene_number}</strong>
                                                <span className="badge bg-secondary ms-2">
                                                    {challenge.day_type === 'WORKING' ? 'Día laborable' : 'Día festivo'}
                                                </span>
                                            </div>
                                            {challenge.member_name && (
                                                <span className="text-muted small">{challenge.member_name}</span>
                                            )}
                                        </div>
                                        <div className="row">
                                            <div className="col-md-6 mb-2 mb-md-0">
                                                <div style={{ position: 'relative', display: 'inline-block' }}>
                                                    <img
                                                        src="/spatial-experience-1.jpg"
                                                        alt="Plano casa"
                                                        className="img-fluid rounded"
                                                        style={{ maxHeight: '200px' }}
                                                    />
                                                    {positions.map((pos) => (
                                                        <div
                                                            key={pos.memberId}
                                                            style={{
                                                                position: 'absolute',
                                                                left: `${pos.x}%`,
                                                                top: `${pos.y}%`,
                                                                transform: 'translate(-50%, -50%)',
                                                                width: '20px',
                                                                height: '20px',
                                                                backgroundColor: pos.color,
                                                                border: '2px solid #333',
                                                                borderRadius: '50%'
                                                            }}
                                                            title={pos.memberName}
                                                        />
                                                    ))}
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                {challenge.scene_title && (
                                                    <p className="mb-1"><strong>Título:</strong> {challenge.scene_title}</p>
                                                )}
                                                {challenge.scene_text && (
                                                    <p className="mb-2 small">{challenge.scene_text}</p>
                                                )}
                                                {positions.length > 0 && (
                                                    <div className="mb-2">
                                                        <p className="small mb-1"><strong>Leyenda:</strong></p>
                                                        <div className="d-flex flex-wrap gap-2">
                                                            {positions.map((p) => (
                                                                <div key={p.memberId} className="d-flex align-items-center gap-1">
                                                                    <div
                                                                        style={{
                                                                            width: '14px',
                                                                            height: '14px',
                                                                            backgroundColor: p.color,
                                                                            border: '1px solid #333',
                                                                            borderRadius: '50%'
                                                                        }}
                                                                    />
                                                                    <span className="small">{p.memberName}</span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                                {comments.length > 0 && (
                                                    <div className="mt-2">
                                                        <p className="small mb-1"><strong>Comentarios por zona:</strong></p>
                                                        {comments.map((c, idx) => (
                                                            <p key={idx} className="small mb-1">
                                                                <em>{c.memberName} ({c.zone}):</em> {c.answer}
                                                            </p>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                );

            case 'SPATIAL_EXPERIENCE_RESSO_CONFIG':
                return (
                    <div className="row">
                        <div className="col-md-6 mb-2 mb-md-0">
                            <div style={{ position: 'relative', display: 'inline-block' }}>
                                <img
                                    src="/spatial-experience/resso-up.jpg"
                                    alt="Planta superior"
                                    className="img-fluid rounded"
                                    style={{ maxHeight: '300px' }}
                                />
                                {(data as SpatialExperienceRessoConfigChallenge[]).map((challenge) => {
                                    let coords = { x: 50, y: 50 };
                                    try {
                                        coords = JSON.parse(challenge.coords);
                                    } catch { /* ignore */ }

                                    return (
                                        <div
                                            key={challenge.spatial_experience_resso_config_challenge_id}
                                            style={{
                                                position: 'absolute',
                                                left: `${coords.x}%`,
                                                top: `${coords.y}%`,
                                                transform: 'translate(-50%, -50%)',
                                                padding: '2px 6px',
                                                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                                border: '1px solid #333',
                                                borderRadius: '4px',
                                                fontSize: '10px',
                                                whiteSpace: 'nowrap'
                                            }}
                                        >
                                            {challenge.label}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                        <div className="col-md-6">
                            <p className="small mb-1"><strong>Estancias configuradas:</strong></p>
                            <ul className="mb-0 ps-3 small">
                                {(data as SpatialExperienceRessoConfigChallenge[]).map((challenge) => (
                                    <li key={challenge.spatial_experience_resso_config_challenge_id}>
                                        {challenge.label}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                );

            case 'SPATIAL_EXPERIENCE_RESSO':
                // Sort: WORKING first, then by scene_number, then FIRST_FLOOR before SECOND_FLOOR
                const sortedSpatialExperienceResso = [...(data as SpatialExperienceRessoChallenge[])].sort((a, b) => {
                    // WORKING (laborable) first, then HOLIDAY (festivo)
                    if (a.day_type !== b.day_type) {
                        return a.day_type === 'WORKING' ? -1 : 1;
                    }
                    // Then by scene number
                    if (a.scene_number !== b.scene_number) {
                        return a.scene_number - b.scene_number;
                    }
                    // Then by floor: FIRST_FLOOR (planta baja) before SECOND_FLOOR (planta superior)
                    if (a.floor_type !== b.floor_type) {
                        return a.floor_type === 'FIRST_FLOOR' ? -1 : 1;
                    }
                    return 0;
                });
                return (
                    <div>
                        {sortedSpatialExperienceResso.map((challenge) => {
                            let positions: Array<{memberId: string, memberName: string, x: number, y: number, color: string}> = [];
                            try {
                                if (challenge.positions && challenge.positions !== '[]') {
                                    positions = JSON.parse(challenge.positions);
                                }
                            } catch { /* ignore parse errors */ }

                            let enclosures: {enclosure1?: string, enclosure2?: string, enclosure3?: string} = {};
                            try {
                                if (challenge.enclosures) {
                                    enclosures = JSON.parse(challenge.enclosures);
                                }
                            } catch { /* ignore parse errors */ }

                            const hasEnclosures = enclosures.enclosure1 || enclosures.enclosure2 || enclosures.enclosure3;
                            const floorImage = challenge.floor_type === 'FIRST_FLOOR'
                                ? '/spatial-experience/resso-1.jpg'
                                : '/spatial-experience/resso-up.jpg';

                            return (
                                <div className="card mb-3 border-success" key={challenge.spatial_experience_resso_challenge_id}>
                                    <div className="card-body py-2">
                                        <div className="d-flex justify-content-between align-items-start mb-2">
                                            <div>
                                                <strong>Escena {challenge.scene_number}</strong>
                                                <span className="badge bg-secondary ms-2">
                                                    {challenge.day_type === 'WORKING' ? 'Día laborable' : 'Día festivo'}
                                                </span>
                                                <span className="badge bg-info ms-2">
                                                    {challenge.floor_type === 'FIRST_FLOOR' ? 'Planta baja' : 'Planta superior'}
                                                </span>
                                            </div>
                                            {challenge.member_name && (
                                                <span className="text-muted small">{challenge.member_name}</span>
                                            )}
                                        </div>
                                        <div className="row">
                                            <div className="col-md-6 mb-2 mb-md-0">
                                                <div style={{ position: 'relative', display: 'inline-block' }}>
                                                    <img
                                                        src={floorImage}
                                                        alt="Plano casa Resso"
                                                        className="img-fluid rounded"
                                                        style={{ maxHeight: '180px' }}
                                                    />
                                                    {positions.map((pos) => (
                                                        <div
                                                            key={pos.memberId}
                                                            style={{
                                                                position: 'absolute',
                                                                left: `${pos.x}%`,
                                                                top: `${pos.y}%`,
                                                                transform: 'translate(-50%, -50%)',
                                                                width: '18px',
                                                                height: '18px',
                                                                backgroundColor: pos.color,
                                                                border: '2px solid #333',
                                                                borderRadius: '50%'
                                                            }}
                                                            title={pos.memberName}
                                                        />
                                                    ))}
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                {challenge.scene_title && (
                                                    <p className="mb-1"><strong>Título:</strong> {challenge.scene_title}</p>
                                                )}
                                                {challenge.scene_text && (
                                                    <p className="mb-2 small">{challenge.scene_text}</p>
                                                )}
                                                {positions.length > 0 && (
                                                    <div className="mb-2">
                                                        <p className="small mb-1"><strong>Leyenda:</strong></p>
                                                        <div className="d-flex flex-wrap gap-2">
                                                            {positions.map((p) => (
                                                                <div key={p.memberId} className="d-flex align-items-center gap-1">
                                                                    <div
                                                                        style={{
                                                                            width: '14px',
                                                                            height: '14px',
                                                                            backgroundColor: p.color,
                                                                            border: '1px solid #333',
                                                                            borderRadius: '50%'
                                                                        }}
                                                                    />
                                                                    <span className="small">{p.memberName}</span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                                {hasEnclosures && (
                                                    <div className="mt-2">
                                                        <p className="small mb-1"><strong>Cerramientos:</strong></p>
                                                        {enclosures.enclosure1 && (
                                                            <p className="small mb-0">Cocina: {enclosures.enclosure1}</p>
                                                        )}
                                                        {enclosures.enclosure2 && (
                                                            <p className="small mb-0">Baño: {enclosures.enclosure2}</p>
                                                        )}
                                                        {enclosures.enclosure3 && (
                                                            <p className="small mb-0">Habitación: {enclosures.enclosure3}</p>
                                                        )}
                                                    </div>
                                                )}
                                                {challenge.scene_comments && (
                                                    <p className="small mt-2 mb-0"><strong>Comentarios cerramientos:</strong> {challenge.scene_comments}</p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                );

            default:
                return <span className="text-muted fst-italic">Tipo de challenge desconocido: {challengeType}</span>;
        }
    };

    const renderResponseContent = (content: ReportContent) => {
        // For CHALLENGE type, render challenge data instead of response
        if (content.content_type === 'CHALLENGE') {
            return renderChallengeContent(content);
        }

        if (!content.response) {
            return <span className="text-muted fst-italic">Sin respuesta</span>;
        }

        const responseData = content.response.response_content;

        // For IMAGE_COMMENT type, render image + comment (supports array of items or legacy single object)
        if (content.content_type === 'IMAGE_COMMENT') {
            try {
                const parsed = JSON.parse(responseData);
                const items: Array<{ imageUrl?: string; comment?: string }> = Array.isArray(parsed)
                    ? parsed
                    : (parsed && (parsed.imageUrl || parsed.comment) ? [parsed] : []);

                if (items.length === 0) {
                    return <span className="text-muted fst-italic">Sin respuesta</span>;
                }

                return (
                    <div className="d-flex flex-column gap-3">
                        {items.map((item, idx) => (
                            <div key={idx} className="row g-3">
                                {item.imageUrl && (
                                    <div className="col-md-6">
                                        <img
                                            src={item.imageUrl}
                                            alt={`Imagen del usuario ${idx + 1}`}
                                            className="img-fluid rounded"
                                            style={{ maxHeight: '400px', objectFit: 'contain' }}
                                        />
                                    </div>
                                )}
                                <div className={item.imageUrl ? 'col-md-6' : 'col-12'}>
                                    {item.comment && <p className="mb-0">{item.comment}</p>}
                                </div>
                            </div>
                        ))}
                    </div>
                );
            } catch {
                return <span>{responseData}</span>;
            }
        }

        // Handle different response types
        try {
            const parsed = JSON.parse(responseData);

            // Boolean response (e.g. "true" / "false")
            if (typeof parsed === 'boolean') {
                return <span>{parsed ? 'Sí' : 'No'}</span>;
            }

            // Helper to extract readable value from an object
            const extractValue = (obj: Record<string, unknown>): string => {
                const keys = ['selected', 'value', 'answer', 'label', 'name', 'option', 'text', 'content'];
                for (const key of keys) {
                    if (key in obj && obj[key] !== null && obj[key] !== undefined) {
                        const val = obj[key];
                        if (Array.isArray(val)) {
                            return val.map(v => typeof v === 'object' ? extractValue(v as Record<string, unknown>) : String(v)).join(', ');
                        }
                        return String(val);
                    }
                }
                // If no known key, return all values joined
                const values = Object.values(obj).filter(v => v !== null && v !== undefined && typeof v !== 'object');
                return values.length > 0 ? values.join(', ') : '';
            };

            // Helper to get text from a variation object
            const getAnswerText = (textObj: any): string => {
                if (typeof textObj === 'string') return textObj;
                if (textObj && typeof textObj === 'object') {
                    return textObj.singular || textObj.plural || textObj.original || textObj.text || '';
                }
                return String(textObj ?? '');
            };

            // Handle common response structures
            if (parsed && typeof parsed === 'object') {
                // QUESTION_MULTIPLE with selectedAnswers (indices)
                if ('selectedAnswers' in parsed) {
                    const indices: number[] = parsed.selectedAnswers || [];
                    const freeTexts: Record<string, string> = parsed.freeTextAnswers || {};
                    const answers: any[] = (content.question as any)?.answers || [];

                    const resolvedAnswers = indices.map((idx: number) => {
                        const answerObj = answers[idx];
                        const answerText = answerObj ? getAnswerText(answerObj.text) || `Opción ${idx + 1}` : `Opción ${idx + 1}`;
                        const freeText = freeTexts[String(idx)]?.trim();
                        return freeText ? `${answerText}: ${freeText}` : answerText;
                    });

                    if (resolvedAnswers.length === 1) {
                        return <span>{resolvedAnswers[0]}</span>;
                    }
                    return (
                        <ul className="mb-0 ps-3">
                            {resolvedAnswers.map((text, idx) => (
                                <li key={idx}>{text}</li>
                            ))}
                        </ul>
                    );
                }

                if ('selected' in parsed) {
                    const selected = parsed.selected;
                    if (Array.isArray(selected)) {
                        return (
                            <ul className="mb-0 ps-3">
                                {selected.map((item: unknown, idx: number) => (
                                    <li key={idx}>{typeof item === 'object' && item !== null ? extractValue(item as Record<string, unknown>) : String(item)}</li>
                                ))}
                            </ul>
                        );
                    }
                    return <span>{String(selected)}</span>;
                }
                if ('text' in parsed) {
                    return <span>{String(parsed.text)}</span>;
                }
                if (Array.isArray(parsed)) {
                    return (
                        <ul className="mb-0 ps-3">
                            {parsed.map((item, idx) => (
                                <li key={idx}>{typeof item === 'object' && item !== null ? extractValue(item as Record<string, unknown>) : String(item)}</li>
                            ))}
                        </ul>
                    );
                }
                // For other objects, try to extract readable values
                const extracted = extractValue(parsed as Record<string, unknown>);
                if (extracted) {
                    return <span>{extracted}</span>;
                }
                // Last resort: show object keys and values as list
                const entries = Object.entries(parsed).filter(([, v]) => v !== null && v !== undefined);
                if (entries.length > 0) {
                    return (
                        <ul className="mb-0 ps-3">
                            {entries.map(([key, val], idx) => (
                                <li key={idx}><strong>{key}:</strong> {typeof val === 'object' ? extractValue(val as Record<string, unknown>) : String(val)}</li>
                            ))}
                        </ul>
                    );
                }
            }
            return <span>{responseData}</span>;
        } catch {
            // Plain text — handle boolean strings
            if (responseData === 'true') return <span>Sí</span>;
            if (responseData === 'false') return <span>No</span>;
            return <span>{responseData}</span>;
        }
    };

    const countGroupResponses = (group: ReportGroup): { total: number; answered: number } => {
        let total = 0;
        let answered = 0;
        group.sections.forEach(section => {
            section.contents.forEach(content => {
                // Skip TEXT_SIMPLE - these are informational text, not questions
                if (content.content_type === 'TEXT_SIMPLE') return;
                if (content.is_included) {
                    total++;
                    if (content.response) answered++;
                }
            });
        });
        return { total, answered };
    };

    if (isLoading) {
        return (
            <div className="container py-4">
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Cargando...</span>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="container py-4">
                <div className="alert alert-danger">{error}</div>
                <Link to={`/architect/project/${projectId}`} className="btn btn-secondary">
                    <i className="bi bi-arrow-left me-2"></i>
                    Volver al proyecto
                </Link>
            </div>
        );
    }

    if (!report) {
        return null;
    }

    return (
        <div className="container py-4">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <Link to={`/architect/project/${projectId}`} className="btn btn-outline-secondary">
                    <i className="bi bi-arrow-left me-2"></i>
                    Volver
                </Link>
                <div className="text-center">
                    <h1 className="h3 mb-1">{report.project_name}</h1>
                    <p className="text-muted mb-0 small">
                        <span className="me-2">{report.client_email}</span>
                        <span className="me-2">|</span>
                        <span className="me-2">{getProjectTypeText(report.intervention_type)}</span>
                        <span className="me-2">|</span>
                        <span>{getSurfaceText(report.surface)}</span>
                    </p>
                </div>
                {/* Placeholder invisible para mantener el título centrado */}
                <div style={{ width: '120px' }}></div>
            </div>

            {/* Groups */}
            {report.groups.filter(g => g.submitted).length === 0 ? (
                <div className="alert alert-info">No hay grupos enviados en este proyecto.</div>
            ) : (
                <div className="accordion" id="reportAccordion">
                    {report.groups.filter(group => group.submitted).map((group) => {
                        const { total, answered } = countGroupResponses(group);
                        const isExpanded = expandedGroups.includes(group.group_id);

                        return (
                            <div className="card mb-3 shadow-sm" key={group.group_id}>
                                <div
                                    className="card-header d-flex justify-content-between align-items-center py-3"
                                    style={{ cursor: 'pointer', backgroundColor: '#f8f9fa' }}
                                    onClick={() => toggleGroup(group.group_id)}
                                >
                                    <div className="d-flex align-items-center">
                                        <h5 className="mb-0 me-3">{group.group_name}</h5>
                                        {group.submitted && (
                                            <span className="badge bg-success">Enviado</span>
                                        )}
                                    </div>
                                    <div className="d-flex align-items-center gap-2">
                                        {groupPdfUrls[group.group_id] && (
                                            <a
                                                href={groupPdfUrls[group.group_id]}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="btn btn-outline-dark btn-sm"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                Descargar informe
                                            </a>
                                        )}
                                        <i className={`bi bi-chevron-${isExpanded ? 'up' : 'down'} fs-5`}></i>
                                    </div>
                                </div>

                                {isExpanded && (
                                    <div className="card-body">
                                        {group.sections.filter(s => s.enabled).length === 0 ? (
                                            <p className="text-muted text-center">No hay secciones en este grupo.</p>
                                        ) : (
                                            group.sections.filter(section => section.enabled).map((section) => {
                                                const isSectionExpanded = expandedSections.includes(section.section_id);

                                                return (
                                                    <div className="mb-3" key={section.section_id}>
                                                        <div
                                                            className="d-flex justify-content-between align-items-center p-2 bg-light rounded"
                                                            style={{ cursor: 'pointer' }}
                                                            onClick={() => toggleSection(section.section_id)}
                                                        >
                                                            <span className="fw-bold">
                                                                {section.section_name}
                                                            </span>
                                                            <i className={`bi bi-chevron-${isSectionExpanded ? 'up' : 'down'}`}></i>
                                                        </div>

                                                        {isSectionExpanded && (
                                                            <div className="mt-2 ms-3">
                                                                {section.contents.filter(c => c.content_type !== 'TEXT_SIMPLE').length === 0 ? (
                                                                    <p className="text-muted small">No hay contenidos en esta seccion.</p>
                                                                ) : (
                                                                    section.contents
                                                                        .filter(content => content.content_type !== 'TEXT_SIMPLE')
                                                                        .map((content) => (
                                                                        <div
                                                                            className="card mb-2"
                                                                            key={content.content_id}
                                                                            style={{
                                                                                opacity: content.is_included ? 1 : 0.6
                                                                            }}
                                                                        >
                                                                            <div className="card-body py-2">
                                                                                <div className="d-flex justify-content-between align-items-start mb-2">
                                                                                    <div>
                                                                                        <strong>{content.content_name}</strong>
                                                                                        {getContentTypeLabel(content.content_type)}
                                                                                    </div>
                                                                                    <div>
                                                                                        {!content.is_included && (
                                                                                            <span className="badge bg-secondary me-1">
                                                                                                No incluido
                                                                                            </span>
                                                                                        )}
                                                                                        <span className={`badge ${content.is_completed ? 'bg-success' : 'bg-warning text-dark'}`}>
                                                                                            {content.is_completed ? 'Completado' : 'Pendiente'}
                                                                                        </span>
                                                                                    </div>
                                                                                </div>

                                                                                {content.content_type !== 'CHALLENGE' && getQuestionText(content.question) && (
                                                                                    <div className="mt-2 mb-1">
                                                                                        <strong>Pregunta:</strong>{' '}
                                                                                        {stripHtml(getQuestionText(content.question))}
                                                                                    </div>
                                                                                )}

                                                                                <div className="mt-2 p-2 bg-light rounded">
                                                                                    {renderResponseContent(content)}
                                                                                </div>

                                                                                {content.response && (
                                                                                    <small className="text-muted d-block mt-1">
                                                                                        <i className="bi bi-clock me-1"></i>
                                                                                        Respondido: {content.response.created_at}
                                                                                        {content.response.respondent_email && (
                                                                                            <span className="ms-2">
                                                                                                <i className="bi bi-person me-1"></i>
                                                                                                {content.response.respondent_email}
                                                                                            </span>
                                                                                        )}
                                                                                    </small>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    ))
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default ProjectReport;
